import React, { useState } from 'react'
import styled from '@emotion/styled'
import { fundsManagementTracker } from '@mobi/account/Common/Analytics'
import { useHostContextFeature } from '@mobi/account/Hooks'
import { isSelfServePaymentMethod } from '@mobi/component-library/Areas/Deposit/helpers'
import { PaymentMethodsAll } from '@mobi/component-library/Areas/Deposit/types'
import { CustomerServiceCentre } from '@mobi/component-library/Base/Link'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Tooltip } from '@mobi/component-library/Overlays/Tooltip'
import { FeatureFlags } from '@mobi/settings/featureFlags'

export const DepositLimitStatus: React.FC<DepositLimitStatusProps> = ({
  depositAmount,
  minimumDepositAmount,
  isPreCommitmentLimitSet,
  selectedPaymentMethod,
  remainingDepositAmount,
  remainingDepositTransactions,
}) => {
  const isDepositValueVolumeLimitsEnabled = useHostContextFeature(
    FeatureFlags.DEPOSIT_VALUE_VOLUME_LIMITS.key
  )
  const [hasTooltipBeenSeen, setHasTooltipBeenSeen] = useState(true)
  const depositLimitStatusRef = React.useRef<HTMLDivElement>(null)

  if (
    remainingDepositAmount <= 0 ||
    (isDepositValueVolumeLimitsEnabled && remainingDepositTransactions === 0)
  ) {
    return (
      <Container>
        <InfoBox color='orange' size='sm' centerIconVertically>
          You've reached the daily deposit limit. You can make further deposits tomorrow.
        </InfoBox>
      </Container>
    )
  }

  function getPaymentMethodMinimum(paymentMethod: PaymentMethodsAll) {
    switch (paymentMethod) {
      case 'CreditCard':
      case 'PayPal':
      case 'ApplePay':
      case 'GooglePay':
      case 'PayID':
        return minimumDepositAmount
      case 'BPay':
        return 20
      case 'Cash':
      case 'ScanMyBet':
        return 0
    }
  }

  const PaymentMethodMinimum = getPaymentMethodMinimum(selectedPaymentMethod)
  if (remainingDepositAmount < PaymentMethodMinimum) {
    return (
      <Container>
        <InfoBox color='orange' size='md'>
          <div>
            You only have ${remainingDepositAmount} remaining on your deposit limit for today. That
            is below the minimum deposit amount of ${PaymentMethodMinimum}. Please try again
            tomorrow.
          </div>
        </InfoBox>
      </Container>
    )
  }

  const showExceedsLimit =
    depositAmount > remainingDepositAmount && !isSelfServePaymentMethod(selectedPaymentMethod)

  if (!isDepositValueVolumeLimitsEnabled && (showExceedsLimit || isPreCommitmentLimitSet)) {
    const infoBoxColour = showExceedsLimit ? 'red' : 'blue'
    return (
      <Container>
        <div ref={depositLimitStatusRef}>
          <InfoBox color={infoBoxColour} size='sm'>
            <span>
              Your deposit limit for today is <b>${remainingDepositAmount}</b>.{' '}
              <a
                role='button'
                href='#'
                onClick={e => {
                  e.preventDefault()
                  setHasTooltipBeenSeen(false)
                }}
              >
                Learn more
              </a>
            </span>
          </InfoBox>
        </div>
        <Tooltip
          triggerElRef={depositLimitStatusRef}
          tooltipId='deposit-uplift-primary-card'
          hasTooltipBeenSeen={() => {
            return hasTooltipBeenSeen
          }}
          onDismissed={() => {
            setHasTooltipBeenSeen(true)
          }}
          isHidden={hasTooltipBeenSeen}
          color='black'
        >
          <CustomerServiceCentre
            prefixText='Your remaining daily deposit limit considers any daily, weekly, fortnightly or monthly
            deposit limit you have selected for your betting account. It also factors in any TABtouch system limits.
            Visit the Deposit Limit page to view your current settings, or'
            onClickPhoneNumber={() =>
              fundsManagementTracker({ interaction_type: 'Need Assistance', location: 'Deposit' })
            }
          />
        </Tooltip>
      </Container>
    )
  }
  return null
}

type DepositLimitStatusProps = {
  depositAmount: number
  isPreCommitmentLimitSet: boolean
  selectedPaymentMethod: PaymentMethodsAll
  minimumDepositAmount: number
  remainingDepositAmount: number
  remainingDepositTransactions: number
}

export const Container = styled.div({
  paddingBottom: '2rem',
})
